"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOnboardingReward = void 0;
const onboarding_reward_1 = __importStar(require("@app/request/onboarding-reward"));
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const use_can_write_organization_1 = require("@app/util/hooks/use-can-write-organization");
const react_1 = __importDefault(require("react"));
/**
 * The single place the „render nothing" rules live. Everything downstream sees either a
 * reward it can draw or `undefined` — never an EXPIRED one it has to remember to skip.
 *
 * EXPIRED is dropped here rather than filtered later because it cannot be re-claimed, so a
 * card for it would offer a button that can only fail.
 */
const visible = (reward) => {
    if (!(0, util_1.isValue)(reward))
        return undefined;
    return reward.state === onboarding_reward_1.ONBOARDING_REWARD_STATES.AVAILABLE ||
        reward.state === onboarding_reward_1.ONBOARDING_REWARD_STATES.CLAIMED
        ? reward
        : undefined;
};
const initialState = {
    isLoading: true,
    isClaiming: false,
    organizationId: undefined,
    reward: undefined,
};
const reducer = (state, action) => {
    switch (action.type) {
        case 'LOADING_START':
            return { ...state, isLoading: true };
        case 'LOADED':
            return {
                ...state,
                isLoading: false,
                organizationId: action.payload?.id,
                reward: visible(action.payload?.onboardingReward),
            };
        case 'CLAIM_START':
            return { ...state, isClaiming: true };
        case 'CLAIM_END':
            return { ...state, isClaiming: false, reward: visible(action.payload) };
        default:
            return state;
    }
};
/**
 * What the hook answers while it is switched off — for a manager who is not an organization
 * owner as much as for one who has not opened the checklist. `isLoading: false` is the point:
 * the page waits on this flag, and a hook that never queries must not hold the loader up.
 */
const IDLE = {
    isLoading: false,
    reward: undefined,
    isClaiming: false,
    claim: async () => { },
};
/**
 * What it answers while it cannot tell yet WHETHER it will query — the ACL that decides it is
 * still on its way. „Not an organization owner" is a final answer and `IDLE` is right for it;
 * „the ACL has not landed" is not, and answering `isLoading: false` to that lets the page draw
 * the checklist without the reward step and renumber it under the manager once it arrives.
 */
const PENDING = { ...IDLE, isLoading: true };
/**
 * The onboarding bonus: whether there is one, and claiming it.
 *
 * Deliberately NOT part of `OnboardingStateQuery`, even though it hangs off the same
 * workspace. That query re-runs on every websocket-driven change (see `onboarding-signal.ts`)
 * — publishing a schedule fires one event per shift — and `onboardingReward` is answered out
 * of Stripe. This loads once per workspace and again only after a claim.
 *
 * `enabled` mirrors `useOnboardingSteps`: disabled it makes no request at all.
 */
const useOnboardingReward = (enabled) => {
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const canWriteOrganization = (0, use_can_write_organization_1.useCanWriteOrganization)();
    const isAclKnown = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(state.me.acl?.organizations));
    const [state, dispatch] = react_1.default.useReducer(reducer, initialState);
    // The claim needs an organization owner, so for anyone else there is nothing to offer and
    // no reason to ask. Hiding the whole card beats drawing a bonus they cannot take.
    const isArmed = enabled && canWriteOrganization && (0, util_1.isValue)(workspaceId);
    const load = react_1.default.useCallback(async () => {
        if (!(0, util_1.isValue)(workspaceId))
            return undefined;
        // a failed query answers `workspace: null`, which the request turns into `undefined`
        return await (0, onboarding_reward_1.default)({ id: workspaceId });
    }, [workspaceId]);
    react_1.default.useEffect(() => {
        if (!isArmed)
            return;
        let isCancelled = false;
        const action = async () => {
            dispatch({ type: 'LOADING_START' });
            const organization = await load();
            if (isCancelled)
                return;
            dispatch({ type: 'LOADED', payload: organization });
        };
        void action();
        return () => {
            isCancelled = true;
        };
    }, [isArmed, load]);
    const { organizationId, reward, isClaiming } = state;
    const claim = react_1.default.useCallback(async () => {
        if (isClaiming ||
            !(0, util_1.isValue)(organizationId) ||
            reward?.state !== onboarding_reward_1.ONBOARDING_REWARD_STATES.AVAILABLE) {
            return;
        }
        dispatch({ type: 'CLAIM_START' });
        let wasUnavailable = false;
        const claimed = await (0, onboarding_reward_1.requestClaimOnboardingReward)({ organization: organizationId }, (errors) => {
            wasUnavailable = (0, onboarding_reward_1.isRewardUnavailableError)(errors);
        });
        // The organization turned out to have no bonus after all (no coupon for its currency, no
        // Stripe customer). Not breakage — nothing was toasted, and the card just goes away.
        if (wasUnavailable) {
            dispatch({ type: 'CLAIM_END', payload: undefined });
            return;
        }
        // The documented rare race: the claim went through but answered null. Ask the query what
        // the reward actually is rather than guessing.
        if (!(0, util_1.isValue)(claimed)) {
            const organization = await load();
            dispatch({ type: 'CLAIM_END', payload: organization?.onboardingReward ?? undefined });
            return;
        }
        dispatch({ type: 'CLAIM_END', payload: claimed });
    }, [isClaiming, organizationId, reward?.state, load]);
    // an unarmed hook that is only waiting for the ACL still owes the page a loader
    if (!isArmed)
        return enabled && (0, util_1.isValue)(workspaceId) && !isAclKnown ? PENDING : IDLE;
    return {
        isLoading: state.isLoading,
        reward,
        isClaiming,
        claim,
    };
};
exports.useOnboardingReward = useOnboardingReward;
exports.default = exports.useOnboardingReward;
